import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Agent, Task } from '../types';

interface CrewCreationData {
  // Agent data
  agent: {
    name: string;
    role: string;
    goal: string;
    backstory: string;
    tools: string[];
    verbose: boolean;
    allow_delegation: boolean;
    max_iter: string;
    max_execution_time: string;
  };

  // Task data
  task: {
    description: string;
    expected_output: string;
    agent_id: number | null;
    tools: string[];
    context: string[];
    output_file: string;
  };

  // Crew data
  crew: {
    name: string;
    description: string;
    verbose: boolean;
    memory: boolean;
    max_rpm: string;
    max_tokens: string;
    configuration_id: number | null;
  };

  // Created items
  createdAgent: Agent | null;
  createdTask: Task | null;

  // Drag and drop data
  crewAgents: Array<{ id: number; name: string; type: 'agent'; data: Agent }>;
  crewTasks: Array<{ id: number; name: string; type: 'task'; data: Task }>;

  // Step management
  currentStep: number;
  isCreating: boolean;
}

interface CrewCreationContextType {
  data: CrewCreationData;
  updateAgent: (agent: Partial<CrewCreationData['agent']>) => void;
  updateTask: (task: Partial<CrewCreationData['task']>) => void;
  updateCrew: (crew: Partial<CrewCreationData['crew']>) => void;
  setCreatedAgent: (agent: Agent | null) => void;
  setCreatedTask: (task: Task | null) => void;
  setCrewAgents: (
    agents: Array<{ id: number; name: string; type: 'agent'; data: Agent }>
  ) => void;
  setCrewTasks: (
    tasks: Array<{ id: number; name: string; type: 'task'; data: Task }>
  ) => void;
  setCurrentStep: (step: number) => void;
  setIsCreating: (creating: boolean) => void;
  resetData: () => void;
  canProceedToNextStep: (step: number) => boolean;
  removeCrewAgent: (agentId: number) => void;
  removeCrewTask: (taskId: number) => void;
}

const CrewCreationContext = createContext<CrewCreationContextType | undefined>(
  undefined
);

const initialData: CrewCreationData = {
  agent: {
    name: '',
    role: '',
    goal: '',
    backstory: '',
    tools: [],
    verbose: true,
    allow_delegation: true,
    max_iter: '5',
    max_execution_time: '300',
  },
  task: {
    description: '',
    expected_output: '',
    agent_id: null,
    tools: [],
    context: [],
    output_file: '',
  },
  crew: {
    name: '',
    description: '',
    verbose: true,
    memory: false,
    max_rpm: '15',
    max_tokens: '2000',
    configuration_id: null,
  },
  createdAgent: null,
  createdTask: null,
  crewAgents: [],
  crewTasks: [],
  currentStep: 1,
  isCreating: false,
};

export const CrewCreationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<CrewCreationData>(initialData);

  const updateAgent = (agent: Partial<CrewCreationData['agent']>) => {
    setData(prev => ({
      ...prev,
      agent: { ...prev.agent, ...agent },
    }));
  };

  const updateTask = (task: Partial<CrewCreationData['task']>) => {
    setData(prev => ({
      ...prev,
      task: { ...prev.task, ...task },
    }));
  };

  const updateCrew = (crew: Partial<CrewCreationData['crew']>) => {
    setData(prev => ({
      ...prev,
      crew: { ...prev.crew, ...crew },
    }));
  };

  const setCreatedAgent = (agent: Agent | null) => {
    setData(prev => ({ ...prev, createdAgent: agent }));
  };

  const setCreatedTask = (task: Task | null) => {
    setData(prev => ({ ...prev, createdTask: task }));
  };

  const setCrewAgents = (
    agents: Array<{ id: number; name: string; type: 'agent'; data: Agent }>
  ) => {
    setData(prev => ({ ...prev, crewAgents: agents }));
  };

  const setCrewTasks = (
    tasks: Array<{ id: number; name: string; type: 'task'; data: Task }>
  ) => {
    setData(prev => ({ ...prev, crewTasks: tasks }));
  };

  const removeCrewAgent = (agentId: number) => {
    setData(prev => ({
      ...prev,
      crewAgents: prev.crewAgents.filter(agent => agent.id !== agentId),
    }));
  };

  const removeCrewTask = (taskId: number) => {
    setData(prev => ({
      ...prev,
      crewTasks: prev.crewTasks.filter(task => task.id !== taskId),
    }));
  };

  const setCurrentStep = (step: number) => {
    setData(prev => ({ ...prev, currentStep: step }));
  };

  const setIsCreating = (creating: boolean) => {
    setData(prev => ({ ...prev, isCreating: creating }));
  };

  const resetData = () => {
    setData(initialData);
  };

  const canProceedToNextStep = (step: number): boolean => {
    switch (step) {
      case 1: // Agent step
        return !!(data.agent.name.trim() && data.agent.role.trim());
      case 2: // Task step
        return !!(
          data.task.description.trim() && data.task.expected_output.trim()
        );
      case 3: // Crew step
        return !!(
          data.crew.name.trim() &&
          data.crewAgents.length > 0 &&
          data.crewTasks.length > 0
        );
      default:
        return false;
    }
  };

  const value: CrewCreationContextType = {
    data,
    updateAgent,
    updateTask,
    updateCrew,
    setCreatedAgent,
    setCreatedTask,
    setCrewAgents,
    setCrewTasks,
    setCurrentStep,
    setIsCreating,
    resetData,
    canProceedToNextStep,
    removeCrewAgent,
    removeCrewTask,
  };

  return (
    <CrewCreationContext.Provider value={value}>
      {children}
    </CrewCreationContext.Provider>
  );
};

export const useCrewCreation = () => {
  const context = useContext(CrewCreationContext);
  if (context === undefined) {
    throw new Error(
      'useCrewCreation must be used within a CrewCreationProvider'
    );
  }
  return context;
};
